// src/components/Admin/shared/TierEditor.jsx
import React from "react";
import { Plus, Trash2, RotateCcw } from "lucide-react";

const DEFAULT_TIERS = {
  ideal: { min: 85, color: "#4caf50", label: "Ideal" },
  strong: { min: 70, color: "#66bb6a", label: "Strong" },
  highMid: { min: 50, color: "#ffb74d", label: "High mid" },
  lowMid: { min: 30, color: "#ff8a65", label: "Low mid" },
  poor: { min: 0, color: "#f44336", label: "Poor" },
  noData: { min: null, color: "#9e9e9e", label: "No data" },
};

export { DEFAULT_TIERS };

const TierEditor = ({ tierCutoffs = {}, onChange }) => {
  const entries = Object.entries(tierCutoffs);

  const updateTier = (key, field, value) => {
    onChange({
      ...tierCutoffs,
      [key]: { ...tierCutoffs[key], [field]: value },
    });
  };

  const renameTierKey = (oldKey, newKey) => {
    if (newKey === oldKey || !newKey.trim()) return;
    // Preserve order by rebuilding the object
    const updated = {};
    for (const [k, v] of Object.entries(tierCutoffs)) {
      if (k === oldKey) {
        updated[newKey] = v;
      } else {
        updated[k] = v;
      }
    }
    onChange(updated);
  };

  const removeTier = (key) => {
    const updated = { ...tierCutoffs };
    delete updated[key];
    onChange(updated);
  };

  const addTier = () => {
    const newKey = `tier_${Date.now().toString(36)}`;
    onChange({
      ...tierCutoffs,
      [newKey]: { min: 0, color: "#78909c", label: "New Tier" },
    });
  };

  const resetToDefaults = () => {
    onChange({ ...DEFAULT_TIERS });
  };

  return (
    <div className="space-y-3">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Define score ranges and colors for each tier
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetToDefaults}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
          >
            <RotateCcw size={12} />
            Reset defaults
          </button>
        </div>
      </div>

      {/* Tier rows */}
      <div className="space-y-2">
        {entries.map(([key, tier]) => {
          const isNoData = key === "noData";
          return (
            <div
              key={key}
              className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5"
            >
              {/* Color swatch + picker */}
              <div className="relative flex-shrink-0">
                <div
                  className="w-8 h-8 rounded-md border border-gray-300 cursor-pointer"
                  style={{ backgroundColor: tier.color }}
                />
                <input
                  type="color"
                  value={tier.color || "#9e9e9e"}
                  onChange={(e) => updateTier(key, "color", e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  title="Pick color"
                />
              </div>

              {/* Key */}
              <input
                type="text"
                value={key}
                onChange={(e) => {
                  // Sanitize: camelCase, no spaces
                  const sanitized = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                  if (sanitized) renameTierKey(key, sanitized);
                }}
                disabled={isNoData}
                className="w-24 px-2 py-1.5 text-xs font-mono bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="tierKey"
              />

              {/* Label */}
              <input
                type="text"
                value={tier.label || ""}
                onChange={(e) => updateTier(key, "label", e.target.value)}
                className="flex-1 min-w-0 px-2 py-1.5 text-sm bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                placeholder="Display label"
              />

              {/* Min score */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400">Min:</span>
                <input
                  type="number"
                  value={isNoData ? "" : (tier.min ?? "")}
                  onChange={(e) =>
                    updateTier(
                      key,
                      "min",
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  disabled={isNoData}
                  className="w-16 px-2 py-1.5 text-sm bg-white border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="—"
                />
              </div>

              {/* Remove button (not for noData) */}
              {!isNoData ? (
                <button
                  type="button"
                  onClick={() => removeTier(key)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition flex-shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              ) : (
                <div className="w-8" />
              )}
            </div>
          );
        })}
      </div>

      {/* Add tier */}
      <button
        type="button"
        onClick={addTier}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 border border-dashed border-gray-300 hover:border-gray-400 rounded-lg transition"
      >
        <Plus size={14} />
        Add Tier
      </button>
    </div>
  );
};

export default TierEditor;
